import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  Stack,
  Card,
  CardContent,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  CreditCard as CreditCardIcon,
  Folder as FolderIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline as CheckIcon,
  WarningAmberRounded as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getClient,
  deleteClient,
  archiveClient,
  unarchiveClient,
} from '../../services/firebase';
import { Client } from '../../types';
import CaseList from './components/CaseList';
import PaymentHistory from './components/PaymentHistory';
import EditClientDialog from './components/EditClientDialog';
import { formatPhoneNumber, formatDate, formatCurrency } from '../../utils/formatters';
import {
  isEligibleForArchiving,
  getArchiveReasonText,
  getClientBalance,
} from '../../utils/archive';
import { useAuth } from '../../contexts/AuthContext';
import { can } from '../../utils/permissions';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ pt: 5 }}>{children}</Box>}
    </div>
  );
};

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || first.toUpperCase();
};

const avatarColorFor = (name: string): string => {
  const palette = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const ClientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { role, currentUser } = useAuth();
  const canArchive = can(role, 'archiveClient');
  const canDelete = can(role, 'deleteClient');

  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveAction, setArchiveAction] = useState<'archive' | 'unarchive'>('archive');
  const [moreMenu, setMoreMenu] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchClient = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getClient(id);
        if (cancelled) return;

        // Defense-in-depth: even though Firestore rules will reject a
        // cross-owner read, we explicitly verify here so a rule
        // regression or a fetch-cached state can't leak PII to a staff
        // user.
        const ownerUid = (data as { createdBy?: string }).createdBy;
        if (
          role === 'staff' &&
          ownerUid &&
          currentUser?.uid &&
          ownerUid !== currentUser.uid
        ) {
          setClient(null);
          return;
        }

        setClient(data);
      } catch (err) {
        console.error('Error fetching client:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchClient();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, role, currentUser?.uid]);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) =>
    setTabValue(newValue);

  const handleDelete = async () => {
    if (!client?.id) return;
    setBusy(true);
    try {
      await deleteClient(client.id);
      navigate('/');
    } catch (err) {
      console.error('Error deleting client:', err);
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!client?.id) return;
    setBusy(true);
    try {
      if (archiveAction === 'archive') {
        const eligibility = isEligibleForArchiving(client);
        const reason = eligibility.suggestedReason || 'manual';
        await archiveClient(client.id, reason);
      } else {
        await unarchiveClient(client.id);
      }
      const updated = await getClient(client.id);
      setClient(updated);
      setArchiveDialogOpen(false);
    } catch (err) {
      console.error('Error updating archive status:', err);
    } finally {
      setBusy(false);
    }
  };

  const openArchiveDialog = (action: 'archive' | 'unarchive') => {
    setMoreMenu(null);
    setArchiveAction(action);
    setArchiveDialogOpen(true);
  };

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 4, sm: 6, md: 8 },
          py: { xs: 5, md: 6 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 240,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box
        sx={{
          maxWidth: 1280,
          mx: 'auto',
          px: { xs: 4, sm: 6, md: 8 },
          py: { xs: 5, md: 6 },
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 10 }}>
            <Typography
              sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
            >
              Client not found
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 3 }}>
              This client record may have been deleted or the link is incorrect.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/clients')}
            >
              Back to Clients
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const initials = initialsFromName(client.fullName);
  const avatarColor = avatarColorFor(client.fullName);
  const balance = getClientBalance(client);

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        variant="text"
        size="small"
        onClick={() => navigate('/clients')}
        sx={{ mb: 3, ml: -1 }}
      >
        Back to Clients
      </Button>

      {/* Profile header card */}
      <Card sx={{ mb: 5, overflow: 'visible' }}>
        <CardContent sx={{ p: { xs: 5, sm: 6 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 4, md: 5 },
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: avatarColor,
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#FFFFFF',
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.2,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {client.fullName}
                </Typography>
                {client.isArchived && (
                  <Chip
                    icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
                    label={getArchiveReasonText(client.archiveReason || 'archived')}
                    size="small"
                    sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 600 }}
                  />
                )}
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 4 }}
                sx={{ color: 'text.secondary' }}
                useFlexGap
                flexWrap="wrap"
              >
                <Typography sx={{ fontSize: '0.875rem' }}>
                  {client.countryOfOrigin || '—'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PhoneIcon sx={{ fontSize: 14 }} />
                  <Typography
                    component="a"
                    href={`tel:${client.phoneNumber}`}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': { color: 'text.primary', textDecoration: 'underline' },
                    }}
                  >
                    {formatPhoneNumber(client.phoneNumber)}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <EmailIcon sx={{ fontSize: 14 }} />
                  <Typography
                    component="a"
                    href={`mailto:${client.email}`}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'inherit',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 240,
                      '&:hover': { color: 'text.primary', textDecoration: 'underline' },
                    }}
                  >
                    {client.email}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: '0.875rem' }}>
                    Client since {formatDate(client.createdAt.toDate())}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            {/* Action buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                width: { xs: '100%', md: 'auto' },
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Edit
              </Button>
              {canArchive && (
                <Button
                  variant="outlined"
                  startIcon={
                    client.isArchived ? <UnarchiveIcon /> : <ArchiveIcon />
                  }
                  onClick={() =>
                    openArchiveDialog(client.isArchived ? 'unarchive' : 'archive')
                  }
                  sx={{ flex: { xs: 1, md: 'none' } }}
                >
                  {client.isArchived ? 'Unarchive' : 'Archive'}
                </Button>
              )}
              <Tooltip title="More actions" arrow>
                <IconButton
                  onClick={(e) => setMoreMenu(e.currentTarget)}
                  aria-label="More actions"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs + content */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{
            px: { xs: 4, sm: 5 },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tab
            label="Cases"
            icon={<FolderIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            id="client-tab-0"
            aria-controls="client-tabpanel-0"
          />
          <Tab
            label="Payments"
            icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            id="client-tab-1"
            aria-controls="client-tabpanel-1"
          />
        </Tabs>

        <Box sx={{ p: { xs: 4, sm: 5 } }}>
          <TabPanel value={tabValue} index={0}>
            <CaseList cases={client.cases} clientId={client.id} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <PaymentHistory cases={client.cases} />
          </TabPanel>
        </Box>
      </Card>

      {/* More-actions menu */}
      <Menu
        anchorEl={moreMenu}
        open={Boolean(moreMenu)}
        onClose={() => setMoreMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 200,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setMoreMenu(null);
            window.location.href = `tel:${client.phoneNumber}`;
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Call client
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoreMenu(null);
            window.location.href = `mailto:${client.email}`;
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Email client
        </MenuItem>
        {canDelete && <Divider />}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setMoreMenu(null);
              setDeleteDialogOpen(true);
            }}
            sx={{ fontSize: '0.875rem', gap: 1.5, color: '#DC2626' }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
            Delete client
          </MenuItem>
        )}
      </Menu>

      <EditClientDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        client={client}
        onClientUpdated={setClient}
      />

      {/* Delete confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete {client.fullName}?
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            aria-label="Close"
            size="small"
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 2 }}>
            This action cannot be undone. All cases, payments, notes, and documents
            will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleteDialogOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
            }
          >
            {busy ? 'Deleting…' : 'Delete client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive / Unarchive confirmation */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {archiveAction === 'archive' ? 'Archive client?' : 'Restore client?'}
          <IconButton
            onClick={() => setArchiveDialogOpen(false)}
            aria-label="Close"
            size="small"
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 3 }}>
            {archiveAction === 'archive'
              ? `${client.fullName} will be moved to your archived clients.`
              : `${client.fullName} will be restored to your active clients list.`}
          </Typography>

          {archiveAction === 'archive' &&
            (() => {
              const eligibility = isEligibleForArchiving(client);
              return eligibility.eligible ? (
                <Alert
                  severity="success"
                  icon={<CheckIcon sx={{ fontSize: 18 }} />}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
                    Eligible for archiving
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {eligibility.reasons.map((r, i) => (
                      <Typography key={i} component="li" sx={{ fontSize: '0.8125rem' }}>
                        {r}
                      </Typography>
                    ))}
                  </Box>
                </Alert>
              ) : (
                <Alert
                  severity="warning"
                  icon={<WarningIcon sx={{ fontSize: 18 }} />}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 0.5 }}>
                    Has active cases or balance
                  </Typography>
                  {balance > 0 && (
                    <Typography sx={{ fontSize: '0.8125rem' }}>
                      Outstanding balance: {formatCurrency(balance)}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                    You can still archive this client manually.
                  </Typography>
                </Alert>
              );
            })()}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setArchiveDialogOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleArchive}
            color={archiveAction === 'archive' ? 'warning' : 'primary'}
            variant="contained"
            disabled={busy}
            startIcon={
              busy ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
            }
          >
            {busy
              ? archiveAction === 'archive'
                ? 'Archiving…'
                : 'Restoring…'
              : archiveAction === 'archive'
              ? 'Archive client'
              : 'Restore client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientProfile;
